import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { client } from '../lib/sanity'
import { expandEvents, moveOccurrence } from '../lib/recurrence'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import YearHeatmap from '../components/calendar/YearHeatmap'
import MilestoneTimeline from '../components/calendar/MilestoneTimeline'

const pad = n => String(n).padStart(2, '0')
const toDateKey = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`

function buildDayCounts(occurrences) {
    const counts = new Map()
    for (const occ of occurrences) {
        let cursor = new Date(occ.start.getFullYear(), occ.start.getMonth(), occ.start.getDate())
        const endDay = new Date(occ.end.getFullYear(), occ.end.getMonth(), occ.end.getDate())
        while (cursor <= endDay) {
            const key = toDateKey(cursor)
            counts.set(key, (counts.get(key) || 0) + 1)
            cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1)
        }
    }
    return counts
}

export default function YearlyOverview() {
    const navigate = useNavigate()
    const [year, setYear] = useState(new Date().getFullYear())
    const [rawDocs, setRawDocs] = useState([])
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        client
            .fetch(`*[_type == "calendarEvent"]{..., category->{_id, title, color, isMilestone}}`)
            .then(data => { setRawDocs(data); setLoaded(true) })
    }, [])

    const occurrences = useMemo(() => {
        const rangeStart = new Date(year, 0, 1 - 7)
        const rangeEnd = new Date(year, 11, 31 + 7)
        return expandEvents(rawDocs, rangeStart, rangeEnd)
    }, [rawDocs, year])

    const dayCounts = useMemo(() => buildDayCounts(occurrences), [occurrences])
    const milestones = useMemo(() => occurrences.filter(o => o.category?.isMilestone), [occurrences])

    const handleDayClick = (date) => {
        navigate('/admin/calendar', { state: { focusDate: date.toISOString() } })
    }

    const handleReschedule = async (milestone, newStartDay) => {
        const duration = milestone.end.getTime() - milestone.start.getTime()
        const newStart = new Date(newStartDay.getFullYear(), newStartDay.getMonth(), newStartDay.getDate())
        const newEnd = new Date(newStart.getTime() + duration)
        await moveOccurrence(milestone, newStart, newEnd)
        const data = await client.fetch(`*[_type == "calendarEvent"]{..., category->{_id, title, color, isMilestone}}`)
        setRawDocs(data)
    }

    return (
        <div className="h-screen w-full flex flex-col overflow-hidden bg-white dark:bg-zinc-950">
            <div className="flex-shrink-0 flex justify-between items-center gap-3 px-3 py-2 border-b-2 border-black dark:border-white">
                <h2 className="text-lg font-serif font-bold text-black dark:text-white">Yearly Overview</h2>
                <Button as={Link} to="/admin/calendar" variant="secondary" className="!px-3 !py-1.5 text-xs">← Back to Calendar</Button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-3">
                <div className="flex items-center gap-3 mb-4">
                    <Button variant="ghost" onClick={() => setYear((y) => y - 1)}>←</Button>
                    <span className="font-serif text-xl font-bold text-black dark:text-white">{year}</span>
                    <Button variant="ghost" onClick={() => setYear((y) => y + 1)}>→</Button>
                </div>

                {loaded && occurrences.length === 0 ? (
                    <p className="font-mono text-sm text-zinc-500">No events yet this year.</p>
                ) : (
                    <>
                        <Card className="p-4 mb-4">
                            <h3 className="mb-4 font-serif font-bold text-black dark:text-white">Milestones</h3>
                            <MilestoneTimeline year={year} milestones={milestones} onReschedule={handleReschedule} />
                        </Card>

                        <Card className="p-4">
                            <h3 className="mb-4 font-serif font-bold text-black dark:text-white">Activity</h3>
                            <YearHeatmap year={year} dayCounts={dayCounts} onDayClick={handleDayClick} />
                        </Card>
                    </>
                )}
            </div>
        </div>
    )
}
